import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import AppShell from './components/layout/AppShell';

const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const SignUp = lazy(() => import('./pages/SignUp'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Home = lazy(() => import('./pages/Home'));
const Workouts = lazy(() => import('./pages/Workouts'));
const ActiveWorkout = lazy(() => import('./pages/ActiveWorkout'));
const ExerciseLibrary = lazy(() => import('./pages/ExerciseLibrary'));
const LiveTraining = lazy(() => import('./pages/LiveTraining'));
const FormResults = lazy(() => import('./pages/FormResults'));
const Health = lazy(() => import('./pages/Health'));
const Profile = lazy(() => import('./pages/Profile'));
const Badges = lazy(() => import('./pages/Badges'));
const Timers = lazy(() => import('./pages/Timers'));
const ActiveTimer = lazy(() => import('./pages/ActiveTimer'));
const AIChat = lazy(() => import('./pages/AIChat'));
const Shop = lazy(() => import('./pages/Shop'));
const BuildWorkout = lazy(() => import('./pages/BuildWorkout'));
const Templates = lazy(() => import('./pages/Templates'));

function Loading() {
  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-dark-400 text-sm">Loading...</span>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { token, user } = useAuthStore();
  if (!token) return <Navigate to="/landing" replace />;
  if (user && !user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

export default function App() {
  const { token, fetchUser } = useAuthStore();

  useEffect(() => {
    if (token) fetchUser();
  }, [token, fetchUser]);

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/landing" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
          <Route path="/" element={<Home />} />
          <Route path="/workouts" element={<Workouts />} />
          <Route path="/exercises" element={<ExerciseLibrary />} />
          <Route path="/health" element={<Health />} />
          <Route path="/profile" element={<Profile />} />
        </Route>
        <Route path="/workout/active" element={<ProtectedRoute><ActiveWorkout /></ProtectedRoute>} />
        <Route path="/workout/build" element={<ProtectedRoute><BuildWorkout /></ProtectedRoute>} />
        <Route path="/live-training" element={<ProtectedRoute><LiveTraining /></ProtectedRoute>} />
        <Route path="/form-analysis" element={<Navigate to="/live-training" replace />} />
        <Route path="/form-results" element={<ProtectedRoute><FormResults /></ProtectedRoute>} />
        <Route path="/badges" element={<ProtectedRoute><Badges /></ProtectedRoute>} />
        <Route path="/timers" element={<ProtectedRoute><Timers /></ProtectedRoute>} />
        <Route path="/timer/active" element={<ProtectedRoute><ActiveTimer /></ProtectedRoute>} />
        <Route path="/ai-chat" element={<ProtectedRoute><AIChat /></ProtectedRoute>} />
        <Route path="/ai-chat/:conversationId" element={<ProtectedRoute><AIChat /></ProtectedRoute>} />
        <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to={token ? '/' : '/landing'} replace />} />
      </Routes>
    </Suspense>
  );
}
